import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { DiscordAddToServerCta } from './DiscordAddToServerCta';
import { SelectablePill } from './SelectablePill';
import { useOnboardingBrand } from './onboardingBrand';
import { CHANNEL_DISCORD, CHANNEL_OTHER_ID, CHANNELS } from './onboardingOptions';
import { useGettingStartedOnboarding } from './useGettingStartedOnboarding';
import { Input } from '@/Domain/components/Shared/Input/Input';

export const DeploymentStep = () => {
  const { t } = useTranslation('getting_started');
  const {
    deploymentSelection,
    toggleDeploymentChannel,
    deploymentOtherText,
    setDeploymentOtherText,
  } = useGettingStartedOnboarding();
  const { brand } = useOnboardingBrand();
  const reduceMotion = useReducedMotion();
  const otherSelected = deploymentSelection.has(CHANNEL_OTHER_ID);
  // Discord-branded when the user was attributed to Discord (cohort B) or picks it here (cohort A).
  // The whole-flow deep-space background + lockup are driven by the page shell off the same
  // effective brand; this local check also covers the isolated (no-provider) render in tests.
  const discordBranded = brand === 'discord' || deploymentSelection.has(CHANNEL_DISCORD);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2" data-testid="gs-deployment-pills">
        {CHANNELS.map((channel) => (
          <SelectablePill
            key={channel.id}
            label={channel.label}
            icon={channel.icon}
            iconColor={channel.iconColor}
            selected={deploymentSelection.has(channel.id)}
            onToggle={() => toggleDeploymentChannel(channel.id)}
            testId={`gs-deployment-pill-${channel.id}`}
          />
        ))}
      </div>
      {otherSelected && (
        <Input
          type="text"
          value={deploymentOtherText}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setDeploymentOtherText(e.target.value)}
          placeholder={t('deployment_step.other_placeholder')}
          maxLength={200}
          autoComplete="off"
          data-testid="gs-deployment-other-input"
        />
      )}
      <AnimatePresence initial={false}>
        {discordBranded && (
          <motion.div
            key="discord-panel"
            data-brand="discord"
            data-testid="gs-deployment-discord-panel"
            initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="mt-2 flex flex-col gap-4 rounded-xl border border-[var(--qc-discord-border)] bg-[var(--qc-discord-surface)] p-5"
          >
            <p className="text-sm font-medium text-[var(--qc-discord-on-surface)]">
              {t('automatic_flow.discord.deployment_prompt')}
            </p>
            <DiscordAddToServerCta source="deployment_step" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
